export interface ChattyTokens {
  access_token: string;
  refresh_token: string;
}

export interface ChattyUser {
  id: string;
  email: string;
  role: string;
  org_id?: string;
  is_superadmin?: boolean;
  auth_provider?: string;
  display_name?: string | null;
  avatar_url?: string | null;
  email_verified?: boolean;
}

export interface ChattyOrg {
  id: string;
  name: string;
  plan?: string;
  plan_name?: string;
  credits_balance?: number;
  display_credits_balance?: number;
  display_credits_monthly?: number;
  seats_used?: number;
  seats_limit?: number;
}

export interface ChattySession {
  user: ChattyUser;
  org: ChattyOrg;
}

export interface ChattyAuthConfig {
  microsoft_enabled: boolean;
  google_enabled?: boolean;
  email_signup_enabled?: boolean;
  self_signup_enabled?: boolean;
  azure_client_id: string | null;
  google_client_id?: string | null;
}

export type ChattyMicrosoftLoginResult =
  | ({ action: "logged_in" } & ChattySession)
  | {
      action: "needs_signup" | "request_access";
      provider: "microsoft";
      email: string;
      display_name?: string | null;
      suggested_org_name?: string | null;
    };

export type ChattyAuthStatus =
  | "checking"
  | "logged_out"
  | "logged_in"
  | "refresh_failed"
  | "gateway_unavailable";

const KEY_JWT = "chatty-jwt";
const KEY_REFRESH = "chatty-refresh";
const KEY_USER = "chatty-user";
const KEY_ORG = "chatty-org";

export const CHATTY_AUTH_CHANGED_EVENT = "chatty-auth-changed";

function getBrowserOrigin(): string | null {
  if (typeof window === "undefined") return null;
  return window.location?.origin ?? null;
}

export function normalizeChattyUrl(value: string): string {
  const trimmed = value.trim();
  const origin = getBrowserOrigin();

  try {
    return (origin ? new URL(trimmed, origin) : new URL(trimmed))
      .toString()
      .replace(/\/$/, "");
  } catch {
    return trimmed.replace(/\/$/, "");
  }
}

export function getChattyApiBaseUrl(): string {
  return normalizeChattyUrl(
    import.meta.env.VITE_CHATTY_API_BASE_URL || "https://api.chatty-ai.ai",
  );
}

function emitAuthChanged(): void {
  window.dispatchEvent(new CustomEvent(CHATTY_AUTH_CHANGED_EVENT));
}

function safeJson<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function getStoredJwt(): string | null {
  return localStorage.getItem(KEY_JWT);
}

export function getStoredRefresh(): string | null {
  return localStorage.getItem(KEY_REFRESH);
}

export function getStoredUser(): ChattyUser | null {
  return safeJson<ChattyUser>(localStorage.getItem(KEY_USER));
}

export function getStoredOrg(): ChattyOrg | null {
  return safeJson<ChattyOrg>(localStorage.getItem(KEY_ORG));
}

export function storeTokens(tokens: ChattyTokens): void {
  localStorage.setItem(KEY_JWT, tokens.access_token);
  localStorage.setItem(KEY_REFRESH, tokens.refresh_token);
  emitAuthChanged();
}

export function storeSession(session: ChattySession): void {
  localStorage.setItem(KEY_USER, JSON.stringify(session.user));
  localStorage.setItem(KEY_ORG, JSON.stringify(session.org));
  emitAuthChanged();
}

export function clearAuthStorage(): void {
  localStorage.removeItem(KEY_JWT);
  localStorage.removeItem(KEY_REFRESH);
  localStorage.removeItem(KEY_USER);
  localStorage.removeItem(KEY_ORG);
  emitAuthChanged();
}

export function isJwtExpiringSoon(token: string, bufferMs = 120_000): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1] ?? ""));
    const exp = Number(payload.exp ?? 0) * 1000;
    return Date.now() + bufferMs >= exp;
  } catch {
    return true;
  }
}

let refreshPromise: Promise<boolean> | null = null;

export async function getAuthConfig(): Promise<ChattyAuthConfig> {
  const response = await fetch(`${getChattyApiBaseUrl()}/config/auth`);
  if (!response.ok) {
    throw new Error("auth_config_unavailable");
  }
  return (await response.json()) as ChattyAuthConfig;
}

export const loadAuthConfig = getAuthConfig;

export function isMicrosoftAuthAvailable(
  config: ChattyAuthConfig | null,
): boolean {
  return Boolean(config?.microsoft_enabled && config.azure_client_id);
}

export function handleMicrosoftAuthError(error: unknown): string {
  if (typeof error === "object" && error && "errorCode" in error) {
    return String(
      (error as { errorCode?: unknown }).errorCode || "microsoft_login_failed",
    );
  }
  return error instanceof Error ? error.message : "microsoft_login_failed";
}

export async function loginWithPassword(
  email: string,
  password: string,
): Promise<ChattySession> {
  const response = await fetch(`${getChattyApiBaseUrl()}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    let code = "login_failed";
    try {
      code = (await response.json()).error ?? code;
    } catch {
      // ignore malformed error responses
    }
    throw new Error(code);
  }

  const data = (await response.json()) as ChattyTokens & ChattySession;
  storeTokens({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
  });
  storeSession({ user: data.user, org: data.org });
  return { user: data.user, org: data.org };
}

export async function exchangeMicrosoftIdToken(
  idToken: string,
): Promise<ChattyMicrosoftLoginResult> {
  const response = await fetch(`${getChattyApiBaseUrl()}/auth/microsoft`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken }),
  });

  if (!response.ok) {
    let code = "microsoft_login_failed";
    try {
      code = (await response.json()).error ?? code;
    } catch {
      // ignore malformed error responses
    }
    throw new Error(code);
  }

  const data = await response.json();
  if (data.action === "needs_signup" || data.action === "request_access") {
    return data as ChattyMicrosoftLoginResult;
  }

  const session = data as ChattyTokens & ChattySession;
  storeTokens({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
  });
  storeSession({ user: session.user, org: session.org });
  return { action: "logged_in", user: session.user, org: session.org };
}

export const loginWithMicrosoft = exchangeMicrosoftIdToken;

export async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getStoredRefresh();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${getChattyApiBaseUrl()}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) return false;
      const tokens = (await response.json()) as ChattyTokens;
      storeTokens(tokens);
      return true;
    } catch {
      return false;
    }
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export async function getValidJwt(): Promise<string | null> {
  const jwt = getStoredJwt();
  if (!jwt) return null;
  if (!isJwtExpiringSoon(jwt)) return jwt;

  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    clearAuthStorage();
    return null;
  }
  return getStoredJwt();
}

export async function getMe(): Promise<ChattySession | null> {
  const jwt = getStoredJwt();
  if (!jwt) return null;

  try {
    const response = await fetch(`${getChattyApiBaseUrl()}/auth/me`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });

    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        clearAuthStorage();
        return null;
      }
      return getMe();
    }

    if (!response.ok) return null;

    const session = (await response.json()) as ChattySession;
    storeSession(session);
    return session;
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  const refreshToken = getStoredRefresh();
  if (refreshToken) {
    try {
      await fetch(`${getChattyApiBaseUrl()}/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // best-effort logout
    }
  }
  clearAuthStorage();
}

export async function checkGatewayHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${getChattyApiBaseUrl()}/health`);
    return response.ok;
  } catch {
    return false;
  }
}
